import { Button } from "@root/shared/components/Button";
import { Icon } from "@root/shared/components/Icon";
import { ModalTrigger } from "@root/shared/components/ModalTrigger";
import { Link } from "@root/shared/components/Link";
import { TextLink } from "@root/shared/components/TextLink";
import { TextButton } from "@root/shared/components/TextButton";
import { SearchSummary, type SearchSummaryProps } from "./SearchSummary";
import * as styles from "./styles.css";

export type SearchConditionsModalProps = SearchConditionsConfig & {
  registrationStep1Url: string;
  loginUrl: string;
  isLoggedIn: boolean;
  title: string;
  iconName: string;
  editPageLinkText: string;
  conditionGaLabel: string;
  summaryGaLabel: string;
  emptyMessage?: string;
};

export type SearchConditionsConfig = {
  conditions: SearchSummaryProps[];
  editPageUrl: string;
};

const LoggedInModal = ({
  title,
  iconName,
  conditions,
  editPageUrl,
  editPageLinkText,
  conditionGaLabel,
  summaryGaLabel,
  emptyMessage,
}: Pick<
  SearchConditionsModalProps,
  | "title"
  | "iconName"
  | "conditions"
  | "editPageUrl"
  | "editPageLinkText"
  | "conditionGaLabel"
  | "summaryGaLabel"
  | "emptyMessage"
>) => (
  <ModalTrigger
    headerTitle={title}
    renderAction={(open) => (
      <Button
        theme="tertiary"
        leftIcon={<Icon name={iconName} />}
        onClick={open}
        className={styles.button}
        data-ga-action="search_form_favorite_job_conditions"
        data-ga-label={conditionGaLabel}
      >
        {title}
      </Button>
    )}
    spAppearanceType="bottom"
  >
    <>
      <div className={styles.editLinkWrapper}>
        <a href={editPageUrl} className={styles.editLink}>
          {editPageLinkText}
          <Icon name="arrow-light-right" fontSize="13" />
        </a>
      </div>
      {conditions.length > 0 ? (
        conditions.map((summary) => (
          <div
            key={`${summary.searchedAt}-${summary.searchUrl}`}
            className={styles.searchSummaryWrapper}
          >
            <SearchSummary
              searchedAt={summary.searchedAt}
              location={summary.location}
              work={summary.work}
              otherInfo={summary.otherInfo}
              keywords={summary.keywords}
              searchUrl={summary.searchUrl}
              summaryGaLabel={summaryGaLabel}
            />
          </div>
        ))
      ) : (
        <p>{emptyMessage}</p>
      )}
    </>
  </ModalTrigger>
);

const NotLoggedInModal = ({
  title,
  registrationStep1Url,
  loginUrl,
}: Pick<SearchConditionsModalProps, "title" | "registrationStep1Url" | "loginUrl">) => (
  <Button
    as="div"
    disabled
    className={styles.disabledButton}
    rightIcon={
      <ModalTrigger
        spAppearanceType="center"
        pcWidthType="large"
        footerContentStyle="button"
        renderAction={(open) => (
          <button
            type="button"
            className={styles.tooltipButton}
            onClick={open}
            data-ga-action="registration_promotion"
            data-ga-label="search_form_modal"
          >
            <Icon name="tooltip" fontSize="16" />
          </button>
        )}
        renderFooterContent={(close) => (
          <TextButton onClick={close} size="small">
            今はやめておく
          </TextButton>
        )}
      >
        <div className={styles.registerModalInner}>
          <p className={styles.registerInfoText}>便利な検索機能を使いますか？</p>
          <p className={styles.registerDescription}>
            <TextLink href={loginUrl}>ログイン</TextLink>
            または会員登録すると、保存した検索条件や履歴から、
            <span className={styles.quickReSearch}>簡単に再検索</span>できるようになります！
          </p>
          <Link
            theme="primary"
            href={registrationStep1Url}
            className={styles.registerLink}
            data-ga-action="search_form_registration-cv"
          >
            会員登録する
          </Link>
        </div>
      </ModalTrigger>
    }
  >
    {title}
  </Button>
);

export const SearchConditionsModal = (props: SearchConditionsModalProps) => {
  const {
    registrationStep1Url,
    loginUrl,
    isLoggedIn,
    title,
    iconName,
    conditions,
    editPageUrl,
    editPageLinkText,
    conditionGaLabel,
    summaryGaLabel,
    emptyMessage,
  } = props;

  return isLoggedIn ? (
    <LoggedInModal
      title={title}
      iconName={iconName}
      conditions={conditions}
      editPageUrl={editPageUrl}
      editPageLinkText={editPageLinkText}
      conditionGaLabel={conditionGaLabel}
      summaryGaLabel={summaryGaLabel}
      emptyMessage={emptyMessage}
    />
  ) : (
    <NotLoggedInModal
      title={title}
      registrationStep1Url={registrationStep1Url}
      loginUrl={loginUrl}
    />
  );
};
